/* eslint-disable @typescript-eslint/no-misused-promises */
import { useEffect, useMemo, useRef, useState } from 'react';
import { faPlus, faPuzzle, faRotateLeft, faSearch, faUpload } from '@fortawesome/pro-regular-svg-icons';
import { Banner, Button, NotificationDispatch, Tab, TabList, TabPanel, Tabs, TextField, Title } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebouncedCallback } from 'use-debounce';
import { Paginator, SEO } from 'src/common/components';
import { useFieldArray, useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import BannerImage from 'src/images/svgs/categorization-dashboard/merge.svg';
import { ContextType } from 'src/common/enums/categorization-dashboard';
import { StyledBannerContainer, StyledBannerImage, StyledContainer, StyledFormGroup, StyledHeader, StyledLimit, StyledPanel, StyledPanelGroup, StyledTabContent } from './categorization.styles';
import { ruleInitialValue } from './components/rule/rule.constants';
import { useDeleteRuleMutation, useGetCategoriesQuery, useGetContextsQuery, useGetRulesQuery, useGetSourcesQuery } from './categorization.slice';
import { Accordion } from './components/accordion/accordion';
import { Activity } from './activity/activity';
import { StyledForm, StyledRow } from './components/rule/rule.styles';
import { Blueprint } from './blueprint/blueprint';
export const CategorizationDashboard = () => {
  const termRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState<string | undefined>();
  const [categories, setCategories] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [rooms, setRooms] = useState<string[]>([]);
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const skip = (currentPage - 1) * limit;
  const {
    data: categoriesData
  } = useGetCategoriesQuery();
  const {
    data: sources
  } = useGetSourcesQuery();
  const {
    data: contexts
  } = useGetContextsQuery();
  const {
    data,
    isLoading
  } = useGetRulesQuery({
    term,
    categories,
    types,
    rooms,
    skip,
    limit
  }, {
    refetchOnMountOrArgChange: true
  });
  const [removeRule] = useDeleteRuleMutation();
  const isResetDisabled = isLoading || !term && !categories.length && !types.length && !rooms.length;
  const {
    control,
    reset
  } = useForm({
    defaultValues: {
      rules: data?.rules ?? []
    }
  });
  const {
    fields,
    prepend,
    remove
  } = useFieldArray({
    control,
    name: 'rules'
  });
  const handleAddRule = () => {
    prepend(ruleInitialValue);
  };
  const handleRemoveRule = async (id: string, index: number) => {
    if (!id) {
      remove(index);
      return;
    }
    try {
      await removeRule(id).unwrap();
      NotificationDispatch('success', 'Rule successfully removed');
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error?.data?.message);
      }
    }
  };
  useEffect(() => {
    reset({
      rules: data?.rules
    });
  }, [data?.rules, reset]);
  const limitOptions = [10, 20, 50, 100].map(option => ({
    label: option.toString(),
    value: option.toString()
  }));
  const selectedLimit = useMemo(() => limitOptions.find(option => option.value === limit.toString()), [limit, limitOptions]);
  const typeOptions = useMemo(() => Object.values(ContextType).map(type => ({
    label: type,
    value: type
  })), []);
  const selectedTypes = useMemo(() => typeOptions.filter(option => types.includes(option.value)), [typeOptions, types]);
  const roomOptions = useMemo(() => contexts?.map(room => ({
    label: room.title,
    value: room._id
  })) || [], [contexts]);
  const selectedRooms = useMemo(() => roomOptions.filter(option => rooms.includes(option.value)), [rooms, roomOptions]);
  const categoryOptions = useMemo(() => categoriesData?.map(category => ({
    label: category,
    value: category
  })) || [], [categoriesData]);
  const selectedCategories = useMemo(() => categoryOptions.filter(option => categories.includes(option.value)), [categories, categoryOptions]);
  const handleTermChange = useDebouncedCallback(() => {
    const trimmedValue = termRef.current?.value.trim().replace(/\s+/g, ' ');
    setTerm(trimmedValue);
  }, 500, {
    leading: false,
    trailing: true
  });
  const handleReset = () => {
    if (termRef.current) {
      termRef.current.value = '';
    }
    setTerm('');
    setCategories([]);
    setTypes([]);
    setRooms([]);
  };
  return <section data-sentry-component="CategorizationDashboard" data-sentry-source-file="categorization.tsx">
      <SEO title="Admin Categorization Dashboard" description="Categorize VM activity" data-sentry-element="SEO" data-sentry-source-file="categorization.tsx" />
      <Banner data-sentry-element="Banner" data-sentry-source-file="categorization.tsx">
        <StyledBannerContainer data-sentry-element="StyledBannerContainer" data-sentry-source-file="categorization.tsx">
          <Title titleText="Categorization Dashboard" subTitleText="Categorize VM activity" isLeftAlign isInBanner data-sentry-element="Title" data-sentry-source-file="categorization.tsx" />
          <StyledBannerImage data-sentry-element="StyledBannerImage" data-sentry-source-file="categorization.tsx">
            <img src={BannerImage} alt="Categorization Dashboard" />
          </StyledBannerImage>
        </StyledBannerContainer>
      </Banner>
      <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="categorization.tsx">
        <StyledPanelGroup data-sentry-element="StyledPanelGroup" data-sentry-source-file="categorization.tsx">
          <StyledPanel data-sentry-element="StyledPanel" data-sentry-source-file="categorization.tsx">
            <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="categorization.tsx">
              <Tabs defaultValue="rules" data-sentry-element="Tabs" data-sentry-source-file="categorization.tsx">
                <TabList data-sentry-element="TabList" data-sentry-source-file="categorization.tsx">
                  <Tab value="rules" data-sentry-element="Tab" data-sentry-source-file="categorization.tsx">
                    <FontAwesomeIcon icon={faUpload} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="categorization.tsx" /> Rules
                  </Tab>
                  <Tab value="raw-events" data-sentry-element="Tab" data-sentry-source-file="categorization.tsx">
                    <FontAwesomeIcon icon={faPuzzle} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="categorization.tsx" /> Room Blueprint
                  </Tab>
                </TabList>
                <TabPanel value="rules" data-sentry-element="TabPanel" data-sentry-source-file="categorization.tsx">
                  <StyledTabContent data-sentry-element="StyledTabContent" data-sentry-source-file="categorization.tsx">
                    <StyledHeader data-sentry-element="StyledHeader" data-sentry-source-file="categorization.tsx">
                      <StyledForm data-sentry-element="StyledForm" data-sentry-source-file="categorization.tsx">
                        <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="categorization.tsx">
                          <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="categorization.tsx">
                            <div>
                              <Button variant="outlined" color="secondary" onClick={handleAddRule} data-sentry-element="Button" data-sentry-source-file="categorization.tsx">
                                <FontAwesomeIcon icon={faPlus} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="categorization.tsx" />
                                Add rule
                              </Button>

                              <StyledLimit data-sentry-element="StyledLimit" data-sentry-source-file="categorization.tsx">
                                <span>Show</span>
                                <div>
                                  <TextField isMulti={false} isSearchable={false} variant="select" handleSelection={option => setLimit(Number.parseInt(option.value, 10))} options={limitOptions} selected={selectedLimit} defaultValue={limitOptions[0]} data-sentry-element="TextField" data-sentry-source-file="categorization.tsx" />
                                </div>
                                <span>records</span>
                              </StyledLimit>
                            </div>
                          </StyledFormGroup>
                        </StyledRow>
                        <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="categorization.tsx">
                          <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="categorization.tsx">
                            <TextField placeholder="Search for a text" variant="input" leftAdornment={<FontAwesomeIcon icon={faSearch} />} maxLength={500} onChange={handleTermChange} innerRef={termRef} data-sentry-element="TextField" data-sentry-source-file="categorization.tsx" />

                            <TextField placeholder="Categories" variant="select" selected={selectedCategories} options={categoryOptions} isMulti isSearchable handleSelection={option => setCategories(option.map(form => form.value))} data-sentry-element="TextField" data-sentry-source-file="categorization.tsx" />

                            <TextField placeholder="Types" variant="select" selected={selectedTypes} options={typeOptions} isMulti isSearchable={false} handleSelection={option => setTypes(option.map(form => form.value))} data-sentry-element="TextField" data-sentry-source-file="categorization.tsx" />

                            <TextField placeholder="Rooms" variant="select" options={roomOptions} selected={selectedRooms} isMulti isSearchable handleSelection={option => setRooms(option.map(form => form.value))} data-sentry-element="TextField" data-sentry-source-file="categorization.tsx" />

                            <Button color="error" size="small" isDisabled={isResetDisabled} onClick={handleReset} data-sentry-element="Button" data-sentry-source-file="categorization.tsx">
                              <FontAwesomeIcon icon={faRotateLeft} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="categorization.tsx" />
                              Reset
                            </Button>
                          </StyledFormGroup>
                        </StyledRow>
                      </StyledForm>
                    </StyledHeader>

                    {fields.map((field, index) => <Accordion key={field._id} id={field._id} title={field.title} type={field.type} index={index} sources={sources || {}} rooms={roomOptions} open={!field._id} handleRemove={handleRemoveRule} />)}

                    <Paginator offset={skip} itemsPerPage={limit} totalEntries={data?.count ?? 0} currentPage={currentPage} handleOnPageChange={(page: number) => setCurrentPage(page)} data-sentry-element="Paginator" data-sentry-source-file="categorization.tsx" />
                  </StyledTabContent>
                </TabPanel>
                <TabPanel value="raw-events" data-sentry-element="TabPanel" data-sentry-source-file="categorization.tsx">
                  <StyledTabContent data-sentry-element="StyledTabContent" data-sentry-source-file="categorization.tsx">
                    <Blueprint rooms={roomOptions} data-sentry-element="Blueprint" data-sentry-source-file="categorization.tsx" />
                  </StyledTabContent>
                </TabPanel>
              </Tabs>
            </StyledContainer>
          </StyledPanel>

          <StyledPanel data-sentry-element="StyledPanel" data-sentry-source-file="categorization.tsx">
            <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="categorization.tsx">
              <Activity rooms={roomOptions} data-sentry-element="Activity" data-sentry-source-file="categorization.tsx" />
            </StyledContainer>
          </StyledPanel>
        </StyledPanelGroup>
      </StyledContainer>
    </section>;
};